/**
 * SM-1168 — the device-local/backend boundary for meeting preferences.
 *
 * `settings.meetings` holds the whole Jitsi override surface, but only the two
 * keys below are user-facing toggles worth following the user across devices.
 * These helpers are deliberately React-free: the in-app tree hydrates from the
 * profile query it already runs, while the TeamHub settings plugins render
 * outside that provider tree and fetch for themselves, and both need the same
 * pick/apply/compare rules.
 */

import type { SyncedMeetingPreferences } from '../types/api';
import { jitsiDefaultSettings } from '../types/jitsiDefaults';
import { settingsService, type UserSettings } from './settingsService';

export const SYNCED_MEETING_KEYS = ['prejoinConfig.enabled', 'disableShortcuts'] as const;

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value === 'true';
  }
  return fallback;
};

const defaultFor = (key: (typeof SYNCED_MEETING_KEYS)[number]): boolean =>
  toBoolean(jitsiDefaultSettings[key], false);

/**
 * Narrow a full meetings object down to the synced keys, resolving anything
 * missing against the Jitsi defaults.
 *
 * Always returning both keys is load-bearing: `storeProfileData` merges the
 * profile document one level deep, so a partial object here would replace the
 * stored `meetings` rather than extend it.
 */
export const pickSyncedMeetingPreferences = (
  meetings: Partial<UserSettings['meetings']> | SyncedMeetingPreferences | undefined,
): Required<SyncedMeetingPreferences> => {
  const source = (meetings ?? {}) as Record<string, unknown>;
  return {
    'prejoinConfig.enabled': toBoolean(
      source['prejoinConfig.enabled'],
      defaultFor('prejoinConfig.enabled'),
    ),
    disableShortcuts: toBoolean(source.disableShortcuts, defaultFor('disableShortcuts')),
  };
};

export const areMeetingPreferencesEqual = (
  a: SyncedMeetingPreferences | undefined,
  b: SyncedMeetingPreferences | undefined,
): boolean => {
  const left = pickSyncedMeetingPreferences(a);
  const right = pickSyncedMeetingPreferences(b);
  return SYNCED_MEETING_KEYS.every((key) => left[key] === right[key]);
};

/** The synced subset of whatever is currently in the settings service. */
export const getLocalMeetingPreferences = (): Required<SyncedMeetingPreferences> =>
  pickSyncedMeetingPreferences(settingsService.getSettings().meetings);

/**
 * Write backend values into the settings service (and so into localStorage,
 * which is what `buildJitsiConfigOverwrite` reads at meeting launch).
 *
 * Goes through `updateSettings` rather than a settings-manager `updateSetting`
 * on purpose: subscribers refresh their view of `settings` without the change
 * counting as an unsaved user edit, so hydration cannot mark a settings form
 * dirty or trip its auto-persist timer.
 */
export const applySyncedMeetingPreferences = async (
  meetings: SyncedMeetingPreferences,
): Promise<void> => {
  await settingsService.updateSettings(
    { meetings: pickSyncedMeetingPreferences(meetings) },
    { validate: false, persist: true },
  );
};
